import api, { apiList } from "api";
import {
  DEFAULT_SWR_OPTS,
  DEFAULT_SWR_OPTS_FASTER,
  DEFAULT_SWR_OPTS_REFRESH,
  DEFAULT_SWR_OPTS_REFRESH_SLOW,
  PREMIUM_SUBS,
  PRO_SUBS,
  STARTER_SUBS,
  SubscriptionStatus,
} from "common/const";
import {
  Invoice,
  InvoicePaymentMethod,
  PagedData,
  PreviewInvoice,
  ProductPrice,
  ScanUsage,
  ScanUsageSummary,
  StripePaginationData,
  SubscriptionInfo,
  SubscriptionPayment,
  priceProps,
} from "common/types";
import useSWR from "swr";
import { useCurrentOrg } from "./useOrganizations";

export const useProducts = () => {
  const { data, error, mutate } = useSWR(
    [apiList.billing.fetchProducts.url],
    async () => {
      const { data } = await api<ProductPrice[]>(apiList.billing.fetchProducts);

      const starterPriceIds: string[] = [];
      const proPriceIds: string[] = [];
      const premiumPriceIds: string[] = [];

      data.forEach((prd) => {
        prd.prices.forEach((price) => {
          if (price.name.startsWith(STARTER_SUBS)) {
            starterPriceIds.push(price.id);
          }

          if (price.name.startsWith(PRO_SUBS)) {
            proPriceIds.push(price.id);
          }

          if (price.name.startsWith(PREMIUM_SUBS)) {
            premiumPriceIds.push(price.id);
          }
        });
        return prd.prices.find((price) => price.name.startsWith(STARTER_SUBS));
      });

      return { data, starterPriceIds, proPriceIds, premiumPriceIds };
    },
    DEFAULT_SWR_OPTS_REFRESH_SLOW
  );

  return {
    data: data?.data,
    starterPriceIds: data?.starterPriceIds ?? [],
    proPriceIds: data?.proPriceIds ?? [],
    premiumPriceIds: data?.premiumPriceIds ?? [],
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

export const useSubscriptionInfo = () => {
  const { data, error, mutate } = useSWR(
    apiList.billing.fetchSubInfo.url,

    async () => {
      try {
        const { data } = await api<SubscriptionInfo[]>(
          apiList.billing.fetchSubInfo
        );
        return data.sort((a, b) => (a.createTime > b.createTime ? -1 : 1));
      } catch (err: any) {
        if (err.status === 404) {
          return [null];
        } else {
          throw err;
        }
      }
    },
    DEFAULT_SWR_OPTS_REFRESH
  );

  let subsInfo: SubscriptionInfo | undefined | null = undefined;
  let pendingSubsInfo: SubscriptionInfo | undefined | null = undefined;
  if (Array.isArray(data)) {
    if (data.length === 2) {
      subsInfo = data[1];
      pendingSubsInfo = data[0];
    } else if (
      data[0]?.subscriptionStatus === SubscriptionStatus.INCOMPLETE ||
      data[0]?.subscriptionStatus === SubscriptionStatus.PAST_DUE
    ) {
      pendingSubsInfo = data[0];
      subsInfo = data[0];
    } else {
      subsInfo = data[0];
    }
  }

  return {
    subsInfo,
    pendingSubsInfo,
    data: data,
    isLoading: !error && data === undefined,
    isError: error,
    isStarter: subsInfo?.priceName === STARTER_SUBS,
    mutate,
  };
  // if (Array.isArray(data)) {
  //   return {
  //     data: data.sort((a, b) => (a.createTime > b.createTime ? -1 : 1)),
  //     isLoading: !error && data === undefined,
  //     isError: error,
  //     mutate,
  //   };
  // } else {
  //   return {
  //     data: data,
  //     isLoading: !error && data === undefined,
  //     isError: error,
  //     mutate,
  //   };
  // }
};

export const useAllSubscriptionInfo = () => {
  const { data, error, mutate } = useSWR(
    apiList.billing.fetchAllSubInfo.url,
    async () => {
      try {
        const { data } = await api<SubscriptionInfo[]>(
          apiList.billing.fetchAllSubInfo
        );
        return data;
      } catch (err) {
        return [];
      }
    },
    DEFAULT_SWR_OPTS_REFRESH
  );

  return {
    data: data,
    isLoading: !error && data === undefined,
    isError: error,
    mutate,
  };
};

export const useSubscriptionPayment = () => {
  const { data, error, mutate } = useSWR(
    apiList.billing.fetchPaymentInfo.url,
    async () => {
      const { data } = await api<SubscriptionPayment>(
        apiList.billing.fetchPaymentInfo,
        {}
      );
      return data;
    },
    DEFAULT_SWR_OPTS
  );

  return {
    data: data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

export const usePaymentUpdate = () => {
  const { data, error } = useSWR(
    apiList.billing.updatePayment.url,
    async () => {
      const { data } = await api<SubscriptionPayment>(
        apiList.billing.updatePayment,
        {}
      );
      return data;
    },
    DEFAULT_SWR_OPTS
  );

  return {
    data: data,
    isLoading: !error && !data,
    isError: error,
  };
};

export const useDefaultPaymentInfo = () => {
  const { data, error } = useSWR(
    apiList.billing.defaultPaymentInfo.url,
    async () => {
      const { data } = await api<InvoicePaymentMethod | null>(
        apiList.billing.defaultPaymentInfo,
        {}
      );
      return data;
    },
    DEFAULT_SWR_OPTS
  );

  return {
    data: data,
    isLoading: !error && !data,
    isError: error,
  };
};

export const useUsageSummary = () => {
  const { data, error, mutate } = useSWR(
    apiList.billing.fetchUsage.url,
    async () => {
      try {
        const { data } = await api<ScanUsageSummary>(
          apiList.billing.fetchUsageSummary,
          {}
        );
        return data;
      } catch (err: any) {
        if (err.status === 404) {
          return null;
        } else {
          throw err;
        }
      }
    },
    DEFAULT_SWR_OPTS_FASTER
  );

  return {
    data: data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

export const useUsage = (start: string | number, end: string | number) => {
  const { data, error, mutate } = useSWR(
    [apiList.billing.fetchUsage.url, start, end],
    async () => {
      try {
        const { data } = await api<ScanUsage[]>(apiList.billing.fetchUsage, {
          start,
          end,
        });
        return data;
      } catch (err) {
        return [];
      }
    },
    DEFAULT_SWR_OPTS
  );

  return {
    data: data,
    isLoading: !error && data === undefined,
    isError: error,
    mutate,
  };
};

export const useCurrentUsage = (pagination: { page: number; size: number }) => {
  const { organization } = useCurrentOrg();
  const { data, error, mutate } = useSWR(
    [
      apiList.billing.fetchUsage.url,
      organization?.id,
      pagination.page,
      pagination.size,
    ],
    async () => {
      const { data } = await api<PagedData<ScanUsage>>(
        apiList.billing.fetchUsage,
        {
          organizationId: organization?.id,
          page: pagination.page,
          size: pagination.size,
        }
      );
      return data;
    },
    DEFAULT_SWR_OPTS
  );

  return {
    data: data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

export type InvoicePramsProps = {
  startAfter?: string | undefined;
  endingBefore?: string | undefined;
};

export const useInvoices = (params?: InvoicePramsProps | undefined) => {
  const { data, error } = useSWR(
    [apiList.billing.fetchInvoice.url, params ? JSON.stringify(params) : ""],
    async () => {
      const { data } = await api<StripePaginationData<Invoice>>(
        apiList.billing.fetchInvoice,
        {
          limit: 10,
          startAfter: params?.startAfter,
          endingBefore: params?.endingBefore,
        }
      );
      return data;
    },
    DEFAULT_SWR_OPTS
  );

  let hasPrevious = false;
  let hasNext = false;
  if (params?.startAfter && !params?.endingBefore) {
    hasNext = Boolean(data?.hasMore);
    hasPrevious = true;
  }
  if (!params?.startAfter && params?.endingBefore) {
    hasPrevious = Boolean(data?.hasMore);
    hasNext = true;
  }

  if (!params) {
    hasNext = Boolean(data?.hasMore);
  }

  return {
    data: data,
    hasPrevious,
    hasNext,
    isLoading: !error && !data,
    isError: error,
  };
};

export const useInvoicePreview = (priceId: string) => {
  const { data, error, mutate } = useSWR(
    [apiList.billing.invoicePreview.url, priceId],
    async () => {
      if (priceId) {
        const { data } = await api<PreviewInvoice>(
          apiList.billing.invoicePreview,
          {
            priceId,
          }
        );
        return data;
      }
    },
    DEFAULT_SWR_OPTS_REFRESH
  );

  return {
    data: data,
    isLoading: !error && !data,
    isError: error,
    mutate,
  };
};

export const findPlan = (
  priceId: string | undefined,
  products: ProductPrice[] | undefined
) => {
  if (!priceId || !products) {
    return {
      plan: undefined,
      price: undefined,
    };
  }
  const plan = products.find((product) => {
    return product.prices.some((price) => price.id === priceId);
  });
  const price = plan?.prices.find((prices) => prices.id === priceId);

  return {
    plan,
    price,
  };
};

export const findFreeTrial = (products: ProductPrice[]) => {
  const plan = products.find((product) => {
    return product.projectType === "free";
  });
  const price = plan?.prices[0];

  return {
    plan,
    price,
  };
};

export const getIntentByInvoiceStatus = (status: Invoice["status"]) => {
  switch (status) {
    case "draft":
      return "secondary";
    case "open":
      return "info";
    case "paid":
      return "success";
    case "uncollectible":
      return "error";
  }
};
